import { pdpu, gayatri, st } from "./assets/education";
import {
  crowd,
  ev,
  docs,
  own,
  privacy,
  vefi,
  vit,
  fruit,
  vercel,
  dockerify,
} from "./assets/projects";

/* ───────────────────────────────────────────────
   IDENTITY
─────────────────────────────────────────────── */
export const Bio = {
  name: "Jemmy Dalsaniya",
  initials: "JD",
  title: "Software Engineer",
  // rotating subtitle under the headline
  roles: [
    "Full-Stack Developer",
    "Backend & Systems Engineer",
    "React / Next.js Developer",
    "Blockchain Developer",
  ],
  company: "Petpooja",
  location: "Ahmedabad, India",
  available: true,
  // one-line value statement 
  headline:
    "Software Engineer building admin-driven platforms, high-performance analytics dashboards, and full-stack products end to end.",
  description:
    "I'm a Software Engineer at Petpooja, where I architect CRM and analytics systems that process large-scale restaurant data with low latency. I enjoy owning features end to end — from designing schemas and optimizing queries to shipping polished React front-ends. Across roles I've built marketplaces, e-commerce platforms, and blockchain dApps, and I care about clean, maintainable, well-tested code.",
  github: "https://github.com/jemmyasjd",
  resume:
    "https://drive.google.com/file/d/1gW80TR83eD3AIWfghXqxlnuxZB0YCssu/view",
  linkedin: "https://www.linkedin.com/in/jemmy-dalsaniya-29924b255/",
  mail: "jemmydalsaniya@gmail.com",
  phone: "+91 7201996520",
  insta: "https://www.instagram.com/_its_j.d/",
  blog: "https://dev.to/jemmyasjd",
};

/* ───────────────────────────────────────────────
   SKILLS  → rendered as "ON-CHAIN TRAITS"
─────────────────────────────────────────────── */
export const skills = [
  {
    title: "Languages",
    skills: ["C++", "C", "Java", "Python", "JavaScript", "SQL", "Solidity"],
  },
  {
    title: "Web",
    skills: [
      "Next.js",
      "React.js",
      "Node.js",
      "Adonis.js",
      "Nest.js",
      "Express.js",
      "Redux",
      "Flask",
      "Django",
      "REST APIs",
      "HTML5",
      "CSS3",
      "Tailwind",
      "Bootstrap",
      "jQuery",
    ],
  },
  {
    title: "Blockchain",
    skills: ["Solidity", "Hardhat", "Remix", "Ether.js", "Metamask", "Ethereum"],
  },
  {
    title: "Cloud / Data",
    skills: [
      "AWS",
      "ClickHouse",
      "Redis",
      "MongoDB",
      "MySQL",
      "Firebase",
    ],
  },
  {
    title: "Tooling",
    skills: [
      "Git",
      "GitHub",
      "GitLab",
      "Docker",
      "Postman",
      "Figma",
      "Jira",
      "Netlify",
      "Vercel",
    ],
  },
];

/* ───────────────────────────────────────────────
   EXPERIENCE → "RELEASE LOG"
─────────────────────────────────────────────── */
export const experiences = [
  {
    id: 0,
    role: "Software Engineer",
    company: "Petpooja — Prayosha Food Services Pvt. Ltd.",
    date: "Mar 2026 — Present",
    location: "Ahmedabad, IN",
    tag: "CURRENT",
    desc: "Architecting the end-to-end CRM module inside Petpooja's Marketing Automation platform — a fully admin-driven system spanning Overview, Segmentation and Customer List, with configurable KPIs, business rules and access controls.",
    bullets: [
      "Architected a fully admin-driven CRM module across Overview, Segmentation and Customer List — all KPIs, rules and access controls configurable from admin with zero code changes.",
      "Built a Customer Insights Dashboard with outlet-level and HO-level analytics (AOV, order & sales trends, top-selling dishes, customer visit funnel) and cross-outlet Compare Graphs.",
      "Cut API latency and improved dashboard load performance with Redis caching on heavy analytical endpoints.",
      "Engineered a dynamic Segmentation Engine with visit-based and spend-based behavioral segments (VIP, Loyal, Promising, Lost, At Risk… 8 types) with fully admin-configurable thresholds and scoring rules.",
      "Designed and optimised analytical queries on ClickHouse (columnar DB) for low-latency reporting over large-scale restaurant transaction data.",
      "Built the Customer List module with outlet-level order drill-down (frequency, recency, revenue), ensuring 100% data accuracy via optimised SQL and efficient cross-outlet aggregation.",
      "Implemented Marketing Automation Plans with RBAC (general + per-restaurant custom plans) and route-level authorization middleware for tenant-based feature gating with zero hard-coded permissions.",
    ],
    skills: ["Node.js", "ClickHouse", "Redis", "RBAC", "SQL", "REST APIs", "System Design"],
  },
  {
    id: 1,
    role: "Full Stack Developer",
    company: "Hyperlink Infosystem Pvt. Ltd.",
    date: "Jan 2025 — Mar 2026",
    location: "Ahmedabad, IN",
    desc: "Delivered production e-commerce and marketplace platforms end-to-end — from interactive React front-ends and third-party integrations to scalable Next.js admin panels.",
    bullets: [
      "Built Tamreen, a gym-trainer marketplace (Urban-Company style) with day-wise availability scheduling, multi-service dynamic slot generation, MyFatoorah (Arab) payment gateway, and fully API-driven trainer onboarding, KYC verification and payouts with zero manual intervention.",
      "Developed RunSmart, a customizable apparel e-commerce platform with an interactive design UI in React.js / Node.js; integrated DPD real-time tracking, SAGE automated invoicing, and end-to-end Klaviyo event tracking (signup, product views, cart, orders, automated coupons).",
      "Built role-based admin panels for Flickez (Super Admin + Service Provider Admin) and a dedicated Admin Dashboard for the AI Cook app (user management, content, analytics, secure auth flows).",
      "Developed a scalable Next.js admin panel with role-based access and product / order management.",
      "Translated 15+ Figma designs into responsive, production-grade UIs and delivered REST APIs / dashboards with ~25% performance improvement via query optimization.",
    ],
    skills: ["Next.js", "React.js", "Node.js", "MyFatoorah", "Klaviyo", "DPD", "SAGE", "AWS", "RBAC"],
  },
  {
    id: 2,
    role: "MERN Stack Intern",
    company: "BrainyBeams Technologies Pvt. Ltd.",
    date: "May 2024 — Jul 2024",
    location: "Ahmedabad, IN",
    desc: "Built and maintained real-time MERN applications, including Fresh Fruit Hub connecting farmers directly with customers.",
    bullets: [
      "Built and maintained MERN-stack applications, boosting runtime performance ~25% via query optimization and modular design.",
      "Collaborated with a 5-member team using Agile to ship dynamic dashboards and REST APIs with 95% test coverage.",
    ],
    skills: ["React.js", "Redux", "Node.js", "MongoDB", "Express.js", "JavaScript"],
    doc: "https://firebasestorage.googleapis.com/v0/b/portfolio-db406.appspot.com/o/completion.png?alt=media&token=e081cef0-6239-4c49-91f6-df5a2712a5d6",
  },
];

/* ───────────────────────────────────────────────
   PROJECTS → "SELECTED RELEASES"
─────────────────────────────────────────────── */
export const projects = [
  {
    id: 0,
    title: "AppCatalyst",
    tagline: "Cloud-native deployment platform",
    description:
      "A cloud-native deployment platform supporting 100+ parallel Docker containers via AWS ECS with 99.9% uptime. Real-time build-log streaming over Redis Pub/Sub + Socket.io, with artifacts served securely through S3 and an Express reverse proxy. Includes API server, build server, socket server and reverse-proxy orchestration.",
    image: vercel,
    video: "https://www.youtube.com/embed/m_9XBt5dO4c?si=kepWmojLWRifc7mq",
    tags: ["AWS ECS", "AWS ECR", "S3", "Docker", "Redis", "Socket.io", "Express.js", "Next.js"],
    category: "infrastructure",
    github: "https://github.com/jemmyasjd/Vercel-Clone",
  },
  {
    id: 1,
    title: "EvRental",
    tagline: "Decentralized EV rental dApp",
    description:
      "A decentralized EV rental dApp enabling hourly bookings via Ethereum smart contracts for transparent, immutable transactions. Solidity contracts wired to React through Ether.js, with Firebase auth and a production frontend on Netlify. Blockchain-backed supply-chain traceability for each vehicle.",
    image: ev,
    video: "https://www.youtube.com/embed/UiO1_Pzltls?si=jy4CFqrYmhg-khAj",
    tags: ["Solidity", "React.js", "Node.js", "Ether.js", "Firebase", "MongoDB", "Remix"],
    category: "blockchain",
    github: "https://github.com/jemmyasjd/Evrent",
    webapp: "https://ev-rent.netlify.app/",
  },
  {
    id: 2,
    title: "Fruit Hub",
    tagline: "Full-stack e-commerce",
    description:
      "A full-stack e-commerce platform with secure Stripe payments, real-time inventory management and Clerk authentication. CI/CD via Docker + GitHub Actions deploying containerized services on AWS EC2, cutting deployment time by 50%.",
    image: fruit,
    video: "https://www.youtube.com/embed/L7gdudJboik?si=WT8uD38iINx2nqW2",
    tags: ["React.js", "MongoDB", "Node.js", "Express.js", "Stripe", "Clerk", "GitHub Actions", "AWS EC2"],
    category: "web app",
    github: "https://github.com/jemmyasjd/Fruit-Hub",
    webapp: "https://freshfruithub.vercel.app/",
  },
  {
    id: 3,
    title: "Dockerify",
    tagline: "3-tier Docker DevOps guide",
    description:
      "A technical DevOps guide on Dockerizing a 3-tier React–Node–MySQL application — container networking, image creation and environment consistency — hosted on GitHub Pages with 1.2k+ views.",
    image: dockerify,
    tags: ["Docker", "Node.js", "React.js", "MySQL"],
    category: "infrastructure",
    github: "https://github.com/jemmyasjd/Docker_MultiContainer",
    webapp:
      "https://dev.to/jemmyasjd/setting-up-the-multi-container-application-with-docker-5gdj",
  },
  {
    id: 4,
    title: "Crowdfunding",
    tagline: "On-chain crowdfunding",
    description:
      "A secure crowdfunding platform letting creators raise funds from backers, with on-chain payment processing and authentication for secure transactions and data privacy. Responsive React front-end with a Node/Express backend.",
    image: crowd,
    tags: ["Solidity", "Remix", "Ether.js", "Metamask", "React.js", "Node.js", "Express.js"],
    category: "blockchain",
    github: "https://github.com/jemmyasjd/Crowdfunding",
    webapp: "https://crowdfunding-jemmy.netlify.app/",
  },
  {
    id: 5,
    title: "DocSync",
    tagline: "Real-time collaborative docs",
    description:
      "A Google-Docs-style collaborative editor with real-time multi-user editing over Socket.io, a Quill rich-text surface, and a Node/Express + MongoDB backend for scalable storage. Fully responsive React UI.",
    image: docs,
    tags: ["React.js", "MongoDB", "Node.js", "Express.js", "Quill", "Socket.io"],
    category: "web app",
    github: "https://github.com/jemmyasjd/Docs-Clone",
  },
  {
    id: 6,
    title: "HyperBlock",
    tagline: "Blockchain from scratch",
    description:
      "A custom blockchain built from scratch in JavaScript with Redis — blocks, chains, consensus and efficient data storage — a ground-up implementation of blockchain's core primitives.",
    image: own,
    tags: ["Node.js", "JavaScript", "Redis", "Express.js"],
    category: "blockchain",
    github: "https://github.com/jemmyasjd/Own-Blockchain/",
  },
  {
    id: 7,
    title: "Proof of Evidence",
    tagline: "Blockchain evidence vault (VeriFIR)",
    description:
      "A blockchain-based FIR / evidence-management system tackling delays, corruption and lack of transparency in the justice system. Decentralized IPFS + Filecoin storage with immutable, tamper-proof on-chain records for secure, transparent crime reporting.",
    image: vefi,
    tags: ["Solidity", "Polygon", "Ether.js", "IPFS / Filecoin", "Flask", "MySQL", "Tailwind"],
    category: "hackathon",
    github: "https://github.com/jemmyasjd/Hackvengers",
    member: [
      { name: "Dalsaniya Jemmy" },
      { name: "Dev Sapariya" },
      { name: "Ronit Shah" },
      { name: "Ansh Bhimani" },
    ],
  },
  {
    id: 8,
    title: "Privacy Guard",
    tagline: "Real-time T&C analyzer extension",
    description:
      "A Chrome extension that analyzes and simplifies website terms & conditions in real time, surfacing privacy risks with live alerts. Lightweight, broadly compatible, and improved via community-driven feedback.",
    image: privacy,
    tags: ["JavaScript", "Python", "LLM", "LlamaIndex", "MongoDB", "HTML", "CSS"],
    category: "hackathon",
    github: "https://github.com/pujamavadhiya/DotSlash-Tech-Trinity",
    member: [
      { name: "Dalsaniya Jemmy" },
      { name: "Dev Sapariya" },
      { name: "Puja Mavadhiya" },
    ],
  },
  {
    id: 9,
    title: "GestureGram",
    tagline: "Sign-language → speech, in real time",
    description:
      "An AI/ML web app empowering mute individuals through real-time gesture recognition — CNN/RNN/GNN/LSTM models translate sign language into text and speech. Socket.io real-time pipeline, React front-end, Flask backend, with Google Translate + gTTS for natural speech.",
    image: vit,
    tags: ["Flask", "TensorFlow", "OpenCV", "Socket.io", "LSTM", "MediaPipe", "React.js", "gTTS"],
    category: "hackathon",
    github: "https://github.com/jemmyasjd/Vit-DevHouse",
    member: [
      { name: "Dalsaniya Jemmy" },
      { name: "Ronit Shah" },
      { name: "Himanshi Singh" },
      { name: "Shaily Patel" },
    ],
  },
];

/* ───────────────────────────────────────────────
   EDUCATION
─────────────────────────────────────────────── */
export const education = [
  {
    id: 0,
    img: pdpu,
    school: "Pandit Deendayal Energy University",
    location: "Gandhinagar, IN",
    date: "Oct 2021 — Jul 2025",
    grade: "9.49 CGPA",
    degree: "B.Tech, Computer Science & Engineering",
    desc: "B.Tech in Computer Science with a 9.49/10 CGPA. Active across national hackathons (SSIP, DotSlash 6.0, Smart Hackathon, HackVengers) and recipient of the Government of Gujarat TFWS scholarship.",
  },
  {
    id: 1,
    img: gayatri,
    school: "Shree Gayatri Vidhyalaya",
    location: "Ahmedabad, IN",
    date: "Apr 2019 — Apr 2021",
    grade: "95.5%",
    degree: "Gujarat Board (XII), Science",
    desc: "Higher secondary (Science) with 95.5% in Gujarat Board and 99.67% in GUJCET.",
  },
  {
    id: 2,
    img: st,
    school: "St. Mary's School",
    location: "Ahmedabad, IN",
    date: "Apr 2017 — Apr 2019",
    grade: "99.57%",
    degree: "Gujarat Board (X)",
    desc: "Secondary education with 99.57% in Gujarat Board — the foundation of my interest in science and mathematics.",
  },
];

/* ───────────────────────────────────────────────
   ACHIEVEMENTS + CERTIFICATIONS  (new section)
─────────────────────────────────────────────── */
export const achievements = [
  {
    title: "Top 10 — Global Blockchain Hackathon",
    detail: "ABC Hackathon · competed among 3000+ teams worldwide",
  },
  {
    title: "Top 6 — Smart Hackathon",
    detail: "Shahid Bhagat Singh State University · 150+ teams · Feb 2023",
  },
  {
    title: "GATE 2025 Qualified",
    detail: "Computer Science & Engineering",
  },
  {
    title: "AWS Certified Solutions Architect — Associate",
    detail: "20+ hrs cloud architecture · EC2, S3, Lambda, IAM, VPC · May 2025",
  },
  {
    title: "TFWS Scholarship — Govt. of Gujarat",
    detail: "100% tuition-fee waiver for academic performance",
  },
  {
    title: "NPTEL Elite + Gold — Top 1%",
    detail: "99% in Incubation & Entrepreneurship · IIT Bombay",
  },
];

/* legacy export kept for any stray import */
export const TimeLineData = [];
